import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createBlock as _createBlock, withModifiers as _withModifiers, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { key: 2 }
const _hoisted_4 = { key: 3 }
const _hoisted_5 = { for: "bucket-name" }
const _hoisted_6 = { for: "bucket-description" }
const _hoisted_7 = { for: "storage-provider" }
const _hoisted_8 = { key: 1 }
const _hoisted_9 = { for: "lines-ftp" }
const _hoisted_10 = { key: 2 }
const _hoisted_11 = { for: "w-endpoint-url" }
const _hoisted_12 = { for: "w-source-uri" }
const _hoisted_13 = { for: "access-key-id" }
const _hoisted_14 = { for: "secret-access-key" }
const _hoisted_15 = { key: 3 }
const _hoisted_16 = { key: 0 }
const _hoisted_17 = { class: "list-group mb-3" }
const _hoisted_18 = { key: 4 }
const _hoisted_19 = { class: "non-bulleted list-group mb-3" }
const _hoisted_20 = { class: "list-group-item" }
const _hoisted_21 = { class: "list-group-item" }
const _hoisted_22 = { class: "list-group-item" }
const _hoisted_23 = {
  key: 0,
  class: "list-group-item"
}
const _hoisted_24 = { class: "mt-4 mb-1" }
const _hoisted_25 = { class: "mt-3" }
const _hoisted_26 = { key: 0 }
const _hoisted_27 = { key: 1 }
const _hoisted_28 = { key: 2 }
const _hoisted_29 = { key: 0 }
const _hoisted_30 = { key: 1 }
const _hoisted_31 = { key: 2 }
const _hoisted_32 = { key: 0 }
const _hoisted_33 = { key: 1 }

import { computed, ref, watch } from 'vue';
import apolloClient from '@/apollo/client';
import { useMutation } from '@vue/apollo-composable';
import { v4 as uuidv4 } from 'uuid';
import {
  ComputeTaskCreateDocument,
  CouponApplicationInput,
  FileUploadInput,
  UploadsCreateDocument,
} from '@/gql/graphql';
import { ApolloCacheUtils } from '@/utils/cache-utils';
import {
  BAlert,
  BCard,
  BCollapse,
  BButton,
  BInputGroup,
  BForm,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BSpinner,
  BTooltip,
} from 'bootstrap-vue-next';
import { formatFileSize, formatFileName } from '@/utils/file-utils';
import { buildStorageTargetOptions } from '@/utils/storage-provider-options';
import { buildWasabiTransferInput } from '@/utils/wasabi-transfer-input';
import {
  Attributes_FtpTransfer,
  Attributes_BaseSpaceTransfer,
  Attributes_Common,
  Attributes_WasabiTransfer,
} from '@/utils/compute-utils';
import CodeValidation from '@/components/CodeValidation.vue';
import {
  COUPON_TYPE_NAMES,
  COUPON_UNITS,
  IS_DEBUG_MODE,
  COMPUTE_TASK_TYPES,
  UPLOAD_MODES,
  UploadModeType,
} from '@/constants';
import { useUploadsStore } from '@/store/uploads';
import { useUserStore } from '@/store/user';
import { useBaseSpaceStore } from '@/store/basespace';
import CloudRegionSelection from '@/components/compute/CloudRegionSelection.vue';
import DeveloperOptions from '@/components/compute/DeveloperOptions.vue';
import PipelinePicker from '@/components/compute/PipelinePicker.vue';
import { PipelineType } from '@/gql/graphql';

// use uploads store to start new uploads

export default /*@__PURE__*/_defineComponent({
  __name: 'UploadComponent',
  props: {
    selectedUploadOption: {}
  },
  emits: ['started-data-transfer', 'started-web-uploads'],
  setup(__props: any, { emit: __emit }) {

const uploadsStore = useUploadsStore();
const userStore = useUserStore();
const bsStore = useBaseSpaceStore();

// --- Component references ---
const devOptions = ref<InstanceType<typeof DeveloperOptions>>();

// --- Component properties ---
const props = __props;

// --- Component emits ---
const emits = __emit;

// --- Local-Only State ---
const files = ref<File[]>([]);
const isDragOver = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);

// --- NextFlow Pipeline specific state ---
const selectedPipeline = ref<PipelineType | null>(null);
const nfCustomWorkflow = ref<boolean>(false);
const nfWorkflowLocation = ref<string>('');

// --- Upload Data ---
const codeValidation = ref<InstanceType<typeof CodeValidation>>();
const newBucketName = ref('');
const newBucketDescription = ref('');
const NewBucketRegionPrefix = ref<string | null>(null);
const customCloudRegionRequired = ref(false);
const selectedStorageTarget = ref('gcp:');
const storageTargetOptions = computed(() => {
  const environments =
    userStore.activeOrgAssociation?.organization?.cloudEnvironments || [];
  return buildStorageTargetOptions(environments);
});
const selectedStorageProvider = computed(
  () => selectedStorageTarget.value.split(':', 1)[0]
);
const selectedCloudEnvironmentId = computed(() => {
  const rawId = selectedStorageTarget.value.split(':')[1];
  return rawId ? Number(rawId) : null;
});
const uploadWeb = computed(
  () => props.selectedUploadOption === UPLOAD_MODES.WEB
);
const uploadFtp = computed(
  () => props.selectedUploadOption === UPLOAD_MODES.FTP
);
const uploadWasabi = computed(
  () => props.selectedUploadOption === UPLOAD_MODES.WASABI
);
const uploadBasespace = computed(
  () => props.selectedUploadOption === UPLOAD_MODES.BASESPACE
);
const componentTitle = computed(() => {
  if (uploadWeb.value) {
    return 'Upload Data from Your Computer';
  } else if (uploadFtp.value) {
    return 'Transfer Data from an FTP Server';
  } else if (uploadWasabi.value) {
    return 'Transfer Data from Wasabi S3';
  } else if (uploadBasespace.value) {
    return 'Transfer Data from BaseSpace';
  }
  // otherwise
  return 'Upload New Data';
});

// --- FTP Transfer Command Examples ---
// 2024-12 "lftp -c 'set ssl:verify-certificate no; set ftp:ssl-force true; set ftp:ssl-protect-data true; open -u user,password ftp://34.71.95.61:2121; mirror -c 225-C1x-280; quit'"
// 2025-05 "lftp -c 'set sftp:auto-confirm yes;set net:max-retries 20;open sftp://X202SC25044156-Z01-F001:xs2asdr6@usftp23.novogene.com; mirror --verbose --use-pget-n=8 -c'"
// 2025-09 "lftp -c 'set ssl:verify-certificate no; set ftp:ssl-force true; set ftp:ssl-protect-data true; open -u user,password ftp://34.71.95.61:2121; mirror -c 225-C1x-280; quit'"
// 2025-09 "lftp -c 'set sftp:auto-confirm yes; set net:max-retries 20; open sftp://ftp:password@34.71.95.61:22; mirror --verbose --use-pget-n=8 -c data/'"
const linesFtp = ref(
  IS_DEBUG_MODE || userStore.developer
    ? "lftp -c 'set ssl:verify-certificate no; set ftp:ssl-force true; set ftp:ssl-protect-data true; open -u user,password ftp://34.71.95.61:2121; mirror -c s1-4-v2; quit'"
    : ''
);

const wasabiSourceUri = ref('');
const wasabiEndpointUrl = ref('');
const wasabiAccessKeyId = ref('');
const wasabiSecretAccessKey = ref('');

const uploadWebBcl = computed(() => {
  return files.value.some((file) => {
    const fileName = file.name.toLowerCase();
    return fileName.endsWith('.bcl') || fileName.endsWith('.bcl.tar');
  });
});

// require even number of fastq files for web upload
const uploadWebOddFastqFilesCount = computed(() => {
  if (!uploadWeb.value) {
    return false;
  }
  // count number of files with fastq or fastq.gz extension
  const fastqFiles = files.value.filter((file) => {
    const fileName = file.name.toLowerCase();
    return fileName.endsWith('.fastq') || fileName.endsWith('.fastq.gz');
  });
  // check if the number of fastq files is odd
  return fastqFiles.length % 2 !== 0;
});
const autoFeaturesDisabled = computed(() => {
  // disabled on multiple kit/coupon codes
  if (
    codeValidation.value &&
    codeValidation.value.codeValidationItems &&
    codeValidation.value.codeValidationItems.length > 1
  ) {
    return true;
  }

  // disabled on WEB upload
  if (uploadWeb.value) {
    return true;
  }

  // otherwise, auto-features are enabled
  return false;
});
const autoSampleAnnotation = ref(false);
const autoProjectDefinition = ref(false);
const autoSampleReconstruction = ref(false);

watch(autoSampleAnnotation, (newAutoSampleAnnotation) => {
  if (!newAutoSampleAnnotation) {
    autoProjectDefinition.value = false;
  }
});

watch(autoProjectDefinition, (newAutoProjectDefinition) => {
  if (newAutoProjectDefinition) {
    autoSampleAnnotation.value = true;
  } else {
    autoSampleReconstruction.value = false;
  }
});

watch(autoSampleReconstruction, (newAutoSampleReconstruction) => {
  if (newAutoSampleReconstruction) {
    autoProjectDefinition.value = true;
  }
});

// --- Storage Coupon Units ---
const minRequiredStorageCouponUnits = computed(() => {
  // WEB upload: require 1 sample for each 2 uploaded fastq files
  if (uploadWeb.value) {
    // return number of files with fastq or fastq.gz extension divided by 2
    const fastqFiles = files.value.filter((file) => {
      const fileName = file.name.toLowerCase();
      return fileName.endsWith('.fastq') || fileName.endsWith('.fastq.gz');
    });
    // return fastqFiles.length / 2, but minimum 1
    let minUnits = Math.ceil(fastqFiles.length / 2);
    if (minUnits < 1) {
      minUnits = 1;
    }
    return minUnits;
  }
  // FTP transfer: require at least 1 sample per line
  else if (uploadFtp.value) {
    return linesFtp.value.split('\n').filter((line) => line).length;
  }
  // WASABI transfer: require at least 1 sample
  else if (uploadWasabi.value) {
    return 1; // require at least 1 sample for Wasabi transfer
  }
  // BaseSpace upload: require at least 1 sample
  return bsStore.bsSelection.samples.length || 1;
});

const declaredStorageCouponUnits = computed(() => {
  return codeValidation.value?.codeValidationItems.reduce(
    (acc, item) => acc + item.unitsDeclared,
    0
  );
});

watch(
  () => props.selectedUploadOption,
  (newValue) => {
    if (newValue === UPLOAD_MODES.WEB) {
      autoSampleAnnotation.value = false;
      autoProjectDefinition.value = false;
      autoSampleReconstruction.value = false;
    }
  }
);

watch(
  () => codeValidation.value?.codeValidationItems.length,
  (newLength) => {
    if (newLength !== undefined) {
      if (newLength > 1) {
        autoSampleAnnotation.value = false;
        autoProjectDefinition.value = false;
        autoSampleReconstruction.value = false;
      }
    }
  }
);

// --- Mutations State ---
const mutationInProgress = ref<boolean>(false);
const mutationError = ref<string | null>(null);
const mutationShowSuccess = ref<boolean>(false);

// --- Mutation: Create Compute Task (BaseSpace/FTP/Wasabi Transfer) ---
const {
  mutate: executeComputeTaskCreateMutation,
  onDone: onComputeTaskCreateMutationDone,
  onError: onComputeTaskCreateMutationError,
} = useMutation(ComputeTaskCreateDocument);

onComputeTaskCreateMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.computeTaskCreate) {
    // BaseSpace of FTP data transfer started successfully
    console.debug('Data transfer task created:', res.data.computeTaskCreate.id);
    emits('started-data-transfer');

    // Add the new bucket to the current user's cached bucketsAssociated list.
    if (res.data.computeTaskCreate.bucket && userStore.id) {
      ApolloCacheUtils.addUserBucket(
        apolloClient.cache,
        userStore.id,
        res.data.computeTaskCreate.bucket
      );
    }

    // report success
    mutationShowSuccess.value = true;
    setTimeout(() => {
      mutationShowSuccess.value = false;
    }, 1000);
  } else {
    mutationError.value = 'FTP transfer init failed: ' + res.data;
    console.error('FTP transfer initialization failed.');
  }
});

onComputeTaskCreateMutationError((err) => {
  console.error('Error starting FTP transfer:', err.message);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Mutation: Create Uploads (WEB Upload) ---
const {
  mutate: executeWebUploadsStartMutation,
  onDone: onWebUploadsStartMutationDone,
  onError: onWebUploadsStartMutationError,
} = useMutation(UploadsCreateDocument);

onWebUploadsStartMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.uploadsCreate) {
    const createdUploadCount = res.data.uploadsCreate.length;
    console.debug('WEB upload records created:', createdUploadCount);

    // Add the new bucket to the current user's cached bucketsAssociated list.
    if (res.data.uploadsCreate.length > 0 && userStore.id) {
      if (res.data.uploadsCreate[0]?.bucket) {
        ApolloCacheUtils.addUserBucket(
          apolloClient.cache,
          userStore.id,
          res.data.uploadsCreate[0].bucket
        );
      }
    }

    // start uploading
    for (let i = 0; i < res.data.uploadsCreate.length; i++) {
      const upload = res.data.uploadsCreate[i];
      if (!upload?.id) {
        console.error('Upload record is missing its ID at index:', i);
        continue;
      }
      // find corresponding item in files
      const file = files.value.find(
        (f) => f.name === formatFileName(upload.sourceFileName)
      );
      if (!file) {
        console.error(
          'File not found for:',
          formatFileName(upload.sourceFileName)
        );
        continue;
      }
      // start the upload
      console.debug('Uploading file:', file.name, file.size);
      void uploadsStore.resumeUpload(upload, file).catch((error) => {
        mutationError.value =
          error instanceof Error
            ? error.message
            : 'Upload could not be started.';
      });
    }

    // report success
    emits('started-web-uploads');
    mutationShowSuccess.value = true;
    setTimeout(() => {
      mutationShowSuccess.value = false;
    }, 1000);
  } else {
    mutationError.value = 'WEB uploads init failed: ' + res.data;
    console.error('WEB upload initialization failed.');
  }
});

onWebUploadsStartMutationError((err) => {
  console.error('Error starting WEB uploads:', err.message);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handlers for the Drag & Drop and File Input ---
function onDragOver(event: DragEvent) {
  event.preventDefault();
  isDragOver.value = true;
}

function onDragLeave() {
  isDragOver.value = false;
}

function onDrop(event: DragEvent) {
  isDragOver.value = false;
  if (event.dataTransfer) {
    const droppedFiles = event.dataTransfer.files;
    addFiles(droppedFiles);
  }
}

function handleFileSelect(event: Event) {
  const input = event.target as HTMLInputElement;
  if (input.files) {
    addFiles(input.files);
  }
}

function addFiles(newFiles: FileList) {
  for (let i = 0; i < newFiles.length; i++) {
    const file = newFiles.item(i);
    if (file) {
      files.value.push(file);
    }
  }
}

function browseFiles() {
  fileInput.value?.click();
}

function onStartUpload() {
  if (uploadWeb.value) {
    startWebUpload();
  } else {
    startDataTransfer();
  }
}

function startDataTransfer() {
  console.debug('Data transfer requested:', props.selectedUploadOption);
  mutationInProgress.value = true;
  mutationError.value = null;

  if (devOptions.value?.extraAttributesInvalid) {
    mutationError.value =
      'Developer JSON attributes must be a valid JSON object.';
    mutationInProgress.value = false;
    return;
  }

  const couponApplications: CouponApplicationInput[] =
    codeValidation?.value?.codeValidationItems.map((item) => ({
      code: item.codeString,
      unitsDeclared: item.unitsDeclared,
    })) || [];

  // define attributes: 1. common attributes
  const attributes:
    | Attributes_Common
    | Attributes_BaseSpaceTransfer
    | Attributes_FtpTransfer
    | Attributes_WasabiTransfer = {
    gceRequestId: uuidv4(),
    newBucketFriendlyName: newBucketName.value,
    newBucketDescription: newBucketDescription.value,
    createProject: autoProjectDefinition.value,
    reconstructData: autoSampleReconstruction.value,
  };

  // FTP/BaseSpace/Wasabi task-specific: attributes, type, name and description
  let taskType: string;
  let taskName: string;
  let taskDescription: string;

  if (props.selectedUploadOption === UPLOAD_MODES.FTP) {
    attributes.ftpCmd = linesFtp.value;
    attributes.autoAnnotate = autoSampleAnnotation.value;
    taskType = COMPUTE_TASK_TYPES.FTP_TRANSFER;
    taskName = 'FTP Transfer for ' + newBucketName.value;
    taskDescription = 'FTP transfer of ' + newBucketName.value + ' data';
  } else if (props.selectedUploadOption === UPLOAD_MODES.BASESPACE) {
    attributes.bsFilesList = bsStore.bsTransferItems;
    taskType = COMPUTE_TASK_TYPES.BASESPACE_TRANSFER;
    taskName = 'BaseSpace Data Transfer for ' + newBucketName.value;
    taskDescription =
      'Transfer of ' +
      newBucketName.value +
      " data from Illumina's BaseSpace Sequence Hub";
  } else if (props.selectedUploadOption === UPLOAD_MODES.WASABI) {
    attributes.wasabiTransfer = buildWasabiTransferInput({
      sourceUri: wasabiSourceUri.value,
      endpointUrl: wasabiEndpointUrl.value,
      accessKeyId: wasabiAccessKeyId.value,
      secretAccessKey: wasabiSecretAccessKey.value,
    });
    attributes.autoAnnotate = autoSampleAnnotation.value;
    taskType = COMPUTE_TASK_TYPES.WASABI_TRANSFER;
    taskName = 'Wasabi Data Transfer for ' + newBucketName.value;
    taskDescription =
      'Transfer of ' + newBucketName.value + ' data from Wasabi S3 storage';
  } else {
    console.error(
      'Unknown upload mode for data transfer:',
      props.selectedUploadOption
    );
    return;
  }

  // pipeline selection for auto-sample reconstruction
  if (attributes.reconstructData) {
    if (nfCustomWorkflow.value) {
      attributes.workflowLocation = nfWorkflowLocation.value;
    } else if (selectedPipeline.value && selectedPipeline.value.id) {
      attributes.pipelineId = selectedPipeline.value.id;
    }
  }

  // add developer options
  if (userStore.developer && devOptions.value?.expanded) {
    // general - version
    if (devOptions.value.version) {
      attributes.version = devOptions.value.version;
    }

    // general - vm lifecycle
    if (devOptions.value.vmLifecycle) {
      attributes.vmLifecycle = devOptions.value.vmLifecycle;
    }

    // general - machine type
    if (devOptions.value.machineType) {
      attributes.machineType = devOptions.value.machineType;
    }

    // extra attributes from JSON
    if (devOptions.value.extraAttributes) {
      Object.assign(attributes, devOptions.value.extraAttributes);
    }
  }

  // execute mutation
  console.debug('Starting data transfer task:', taskType);

  // mutationInProgress.value = false;
  executeComputeTaskCreateMutation({
    coupons: couponApplications,
    name: taskName,
    description: taskDescription,
    taskType: taskType,
    attributes: attributes,
    requestedRegionPrefix: NewBucketRegionPrefix.value
      ? NewBucketRegionPrefix.value
      : undefined,
  });
}

function startWebUpload() {
  console.debug(
    'Starting WEB uploads:',
    files.value.length,
    selectedStorageProvider.value
  );
  mutationInProgress.value = true;
  mutationError.value = null;

  const couponApplications: CouponApplicationInput[] =
    codeValidation?.value?.codeValidationItems.map((item) => ({
      code: item.codeString,
      unitsDeclared: item.unitsDeclared,
    })) || [];

  const filesInput: FileUploadInput[] = files.value.map((file) => ({
    sourceFileName: file.name,
    targetFileName: file.name.split('/').pop() || file.name,
    fileSize: file.size.toString(),
  }));

  // execute mutation
  executeWebUploadsStartMutation({
    coupons: couponApplications,
    name: newBucketName.value,
    description: newBucketDescription.value,
    files: filesInput,
    bucketName: null,
    bucketPath: null,
    requestedRegionPrefix: NewBucketRegionPrefix.value
      ? NewBucketRegionPrefix.value
      : null,
    cloudProvider: selectedStorageProvider.value,
    cloudEnvironmentId: selectedCloudEnvironmentId.value,
  });
}

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(BCard), { title: componentTitle.value }, {
      default: _withCtx(() => [
        (uploadWeb.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_1, " Upload new data to the analytics platform directly from your computer via web interface. If upload process gets interrupted, you can resume it later without losing your progress. "))
          : (uploadFtp.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_2, " Transfer data from an FTP server of your sequencing provider to the analytics platform. Once the process is started, you can leave the platform and come back later to check the status. If you have your notifications enabled, you will receive an e-mail when the transfer is complete. "))
            : (uploadWasabi.value)
              ? (_openBlock(), _createElementBlock("p", _hoisted_3, " Transfer data from a Wasabi S3 hot cloud storage to the analytics platform. Once the process is started, you can leave the platform and come back later to check the status. If you have your notifications enabled, you will receive an e-mail when the transfer is complete. "))
              : (uploadBasespace.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_4, " Transfer data from Illumina BaseSpace Sequence Hub to the analytics platform. Once the process is started, you can leave the platform and come back later to check the status. If you have your notifications enabled, you will receive an e-mail when the transfer is complete. "))
                : _createCommentVNode("", true),
        _createElementVNode("div", null, [
          _createVNode(_unref(BForm), {
            onSubmit: _withModifiers(onStartUpload, ["prevent"])
          }, {
            default: _withCtx(() => [
              _cache[67] || (_cache[67] = _createElementVNode("h5", { class: "mt-4" }, "Label your data", -1)),
              _createVNode(_unref(BFormGroup), { class: "mt-3" }, {
                default: _withCtx(() => [
                  _createElementVNode("label", _hoisted_5, [
                    _createVNode(_component_font_awesome_icon, {
                      icon: ['fas', 'tag'],
                      class: "me-1"
                    }),
                    _cache[24] || (_cache[24] = _createTextVNode(" Data label ", -1)),
                    _createVNode(_component_font_awesome_icon, {
                      icon: ['fas', 'question-circle'],
                      class: "ms-2 text-secondary",
                      id: "bucket-name-help-icon"
                    }),
                    _createVNode(_unref(BTooltip), { target: "bucket-name-help-icon" }, {
                      default: _withCtx(() => [...(_cache[23] || (_cache[23] = [
                        _createTextVNode("A label to help you identify your data later. ", -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _createVNode(_unref(BFormInput), {
                    id: "bucket-name",
                    name: "bucketName",
                    modelValue: newBucketName.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((newBucketName).value = $event)),
                    type: "text",
                    required: ""
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }),
              _createVNode(_unref(BFormGroup), { class: "mt-3" }, {
                default: _withCtx(() => [
                  _createElementVNode("label", _hoisted_6, [
                    _createVNode(_component_font_awesome_icon, {
                      icon: ['fas', 'align-justify'],
                      class: "me-1"
                    }),
                    _cache[26] || (_cache[26] = _createTextVNode(" Data description ", -1)),
                    _createVNode(_component_font_awesome_icon, {
                      icon: ['fas', 'question-circle'],
                      class: "ms-2 text-secondary",
                      id: "bucket-description-help-icon"
                    }),
                    _createVNode(_unref(BTooltip), { target: "bucket-description-help-icon" }, {
                      default: _withCtx(() => [...(_cache[25] || (_cache[25] = [
                        _createTextVNode(" Optional description of the data to help you identify your data. ", -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _createVNode(_unref(BFormTextarea), {
                    id: "bucket-description",
                    name: "bucketDescription",
                    modelValue: newBucketDescription.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((newBucketDescription).value = $event)),
                    type: "text",
                    rows: "2",
                    "max-rows": "10"
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }),
              _createVNode(CloudRegionSelection, {
                modelValue: NewBucketRegionPrefix.value,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((NewBucketRegionPrefix).value = $event)),
                modelRequired: customCloudRegionRequired.value,
                "onUpdate:modelRequired": _cache[3] || (_cache[3] = ($event: any) => ((customCloudRegionRequired).value = $event)),
                class: "mt-3 mb-3"
              }, null, 8, ["modelValue", "modelRequired"]),
              (uploadWeb.value)
                ? (_openBlock(), _createBlock(_unref(BFormGroup), {
                    key: 0,
                    class: "mt-3 mb-3"
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("label", _hoisted_7, [
                        _createVNode(_component_font_awesome_icon, {
                          icon: ['fas', 'cloud'],
                          class: "me-1"
                        }),
                        _cache[27] || (_cache[27] = _createTextVNode(" Storage provider ", -1))
                      ]),
                      _createVNode(_unref(BFormSelect), {
                        id: "storage-provider",
                        modelValue: selectedStorageTarget.value,
                        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((selectedStorageTarget).value = $event)),
                        options: storageTargetOptions.value,
                        required: ""
                      }, null, 8, ["modelValue", "options"])
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              (uploadFtp.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                    _createVNode(_unref(BFormGroup), {
                      id: "lines-ftp",
                      class: "mb-2"
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("label", _hoisted_9, [
                          _createVNode(_component_font_awesome_icon, {
                            icon: ['fas', 'code'],
                            class: "me-1"
                          }),
                          _cache[29] || (_cache[29] = _createTextVNode(" Paste complete commands provided by your sequencing provider ", -1)),
                          _createVNode(_component_font_awesome_icon, {
                            icon: ['fas', 'question-circle'],
                            class: "ms-2 text-secondary",
                            id: "lines-ftp-help-icon"
                          }),
                          _createVNode(_unref(BTooltip), {
                            target: "lines-ftp-help-icon",
                            class: "large-tooltip"
                          }, {
                            default: _withCtx(() => [...(_cache[28] || (_cache[28] = [
                              _createElementVNode("div", { class: "text-start" }, [
                                _createElementVNode("p", null, " You can provide multiple commands, one per line, to transfer data from multiple sources. Make sure to include all necessary authentication details (username, password, keys, etc.) in the commands. Double-check the commands before starting the transfer. Incorrect commands may lead to transfer failures or data loss. "),
                                _createElementVNode("p", { class: "mb-1" }, [
                                  _createElementVNode("strong", null, "Here are some examples of valid commands:")
                                ]),
                                _createElementVNode("ul", { style: {"list-style-type":"disc","padding-left":"1rem"} }, [
                                  _createElementVNode("li", { class: "mb-2" }, [
                                    _createElementVNode("code", null, " wget -r -b -c --user=X012SC34567890-Z01-F002 --password=secret ftp://ftp123.genedatabase.com:21/ ")
                                  ]),
                                  _createElementVNode("li", null, [
                                    _createElementVNode("code", null, " lftp -c 'set sftp:auto-confirm yes;set net:max-retries 20;open sftp://X123SC0123456-Z01-F001:secret@ftp123.genedatabase.com; mirror --verbose --use-pget-n=8 -c' ")
                                  ])
                                ])
                              ], -1)
                            ]))]),
                            _: 1
                          })
                        ]),
                        _createVNode(_unref(BFormTextarea), {
                          name: "lines-ftp",
                          modelValue: linesFtp.value,
                          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((linesFtp).value = $event)),
                          rows: "4",
                          "max-rows": "12",
                          required: ""
                        }, null, 8, ["modelValue"])
                      ]),
                      _: 1
                    })
                  ]))
                : _createCommentVNode("", true),
              (uploadWasabi.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                    _cache[36] || (_cache[36] = _createElementVNode("h5", { class: "mt-4" }, "Wasabi Storage Details & Credentials", -1)),
                    _createVNode(_unref(BFormGroup), { class: "mt-3" }, {
                      default: _withCtx(() => [
                        _createElementVNode("label", _hoisted_11, [
                          _createVNode(_component_font_awesome_icon, {
                            icon: ['fas', 'server'],
                            class: "me-1"
                          }),
                          _cache[30] || (_cache[30] = _createTextVNode(" Server ", -1)),
                          _createVNode(_component_font_awesome_icon, {
                            icon: ['fas', 'question-circle'],
                            class: "ms-2 text-secondary",
                            id: "w-endpoint-url-help-icon"
                          })
                        ]),
                        _createVNode(_unref(BTooltip), { target: "w-endpoint-url-help-icon" }, {
                          default: _withCtx(() => [...(_cache[31] || (_cache[31] = [
                            _createTextVNode(" Endpoint URL ", -1)
                          ]))]),
                          _: 1
                        }),
                        _createVNode(_unref(BFormInput), {
                          id: "w-endpoint-url",
                          name: "wEndpointUrl",
                          modelValue: wasabiEndpointUrl.value,
                          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((wasabiEndpointUrl).value = $event)),
                          type: "text",
                          required: ""
                        }, null, 8, ["modelValue"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(BFormGroup), { class: "mt-3" }, {
                      default: _withCtx(() => [
                        _createElementVNode("label", _hoisted_12, [
                          _createVNode(_component_font_awesome_icon, {
                            icon: ['fas', 'link'],
                            class: "me-1"
                          }),
                          _cache[32] || (_cache[32] = _createTextVNode(" AWS CLI Path ", -1)),
                          _createVNode(_component_font_awesome_icon, {
                            icon: ['fas', 'question-circle'],
                            class: "ms-2 text-secondary",
                            id: "w-source-uri-help-icon"
                          })
                        ]),
                        _createVNode(_unref(BTooltip), { target: "w-source-uri-help-icon" }, {
                          default: _withCtx(() => [...(_cache[33] || (_cache[33] = [
                            _createTextVNode(" Source URI ", -1)
                          ]))]),
                          _: 1
                        }),
                        _createVNode(_unref(BFormInput), {
                          id: "w-source-uri",
                          name: "wSourceUri",
                          modelValue: wasabiSourceUri.value,
                          "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((wasabiSourceUri).value = $event)),
                          type: "text",
                          required: ""
                        }, null, 8, ["modelValue"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(BFormGroup), { class: "mt-3" }, {
                      default: _withCtx(() => [
                        _createElementVNode("label", _hoisted_13, [
                          _createVNode(_component_font_awesome_icon, {
                            icon: ['fas', 'fingerprint'],
                            class: "me-1"
                          }),
                          _cache[34] || (_cache[34] = _createTextVNode(" Access Key ID ", -1))
                        ]),
                        _createVNode(_unref(BFormInput), {
                          id: "access-key-id",
                          name: "accessKeyId",
                          modelValue: wasabiAccessKeyId.value,
                          "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((wasabiAccessKeyId).value = $event)),
                          type: "text",
                          required: ""
                        }, null, 8, ["modelValue"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_unref(BFormGroup), { class: "mt-3" }, {
                      default: _withCtx(() => [
                        _createElementVNode("label", _hoisted_14, [
                          _createVNode(_component_font_awesome_icon, {
                            icon: ['fas', 'key'],
                            class: "me-1"
                          }),
                          _cache[35] || (_cache[35] = _createTextVNode(" Secret Access Key ", -1))
                        ]),
                        _createVNode(_unref(BFormInput), {
                          id: "secret-access-key",
                          name: "secretAccessKey",
                          modelValue: wasabiSecretAccessKey.value,
                          "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((wasabiSecretAccessKey).value = $event)),
                          type: "text",
                          required: ""
                        }, null, 8, ["modelValue"])
                      ]),
                      _: 1
                    })
                  ]))
                : _createCommentVNode("", true),
              (uploadWeb.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                    _cache[42] || (_cache[42] = _createElementVNode("h5", null, "Select files for upload", -1)),
                    _createElementVNode("div", {
                      class: _normalizeClass(["drag-drop-area mb-3", { 'drag-over': isDragOver.value }]),
                      onDragover: _withModifiers(onDragOver, ["prevent"]),
                      onDragleave: _withModifiers(onDragLeave, ["prevent"]),
                      onDrop: _withModifiers(onDrop, ["prevent"])
                    }, [
                      _cache[38] || (_cache[38] = _createElementVNode("p", null, "Drag & drop files here", -1)),
                      _cache[39] || (_cache[39] = _createElementVNode("p", null, "or", -1)),
                      _createVNode(_unref(BButton), {
                        variant: "outline-primary",
                        onClick: browseFiles
                      }, {
                        default: _withCtx(() => [...(_cache[37] || (_cache[37] = [
                          _createTextVNode("Browse Files", -1)
                        ]))]),
                        _: 1
                      }),
                      _createElementVNode("input", {
                        type: "file",
                        ref_key: "fileInput",
                        ref: fileInput,
                        multiple: "",
                        onChange: handleFileSelect,
                        class: "d-none"
                      }, null, 544)
                    ], 34),
                    (files.value.length > 0)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                          _cache[40] || (_cache[40] = _createElementVNode("h5", null, "Selected Files", -1)),
                          _createElementVNode("ul", _hoisted_17, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(files.value, (file, index) => {
                              return (_openBlock(), _createElementBlock("li", {
                                key: index,
                                class: "list-group-item"
                              }, _toDisplayString(file.name) + " (" + _toDisplayString(_unref(formatFileSize)(file.size)) + ") ", 1))
                            }), 128))
                          ])
                        ]))
                      : _createCommentVNode("", true),
                    _createVNode(_unref(BAlert), {
                      modelValue: uploadWebOddFastqFilesCount.value,
                      "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((uploadWebOddFastqFilesCount).value = $event)),
                      variant: "danger",
                      class: "mt-3"
                    }, {
                      default: _withCtx(() => [...(_cache[41] || (_cache[41] = [
                        _createTextVNode(" Upload an even number of FASTQ files. Each pair of files will be associated with a sample. ", -1)
                      ]))]),
                      _: 1
                    }, 8, ["modelValue"])
                  ]))
                : _createCommentVNode("", true),
              (uploadBasespace.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                    _cache[47] || (_cache[47] = _createElementVNode("h5", null, "Transfer Summary", -1)),
                    _createElementVNode("ul", _hoisted_19, [
                      _createElementVNode("li", _hoisted_20, [
                        _cache[43] || (_cache[43] = _createElementVNode("strong", null, "Samples:", -1)),
                        _createTextVNode(" " + _toDisplayString(_unref(bsStore).bsSelection.samples.length), 1)
                      ]),
                      _createElementVNode("li", _hoisted_21, [
                        _cache[44] || (_cache[44] = _createElementVNode("strong", null, "Files:", -1)),
                        _createTextVNode(" " + _toDisplayString(_unref(bsStore).bsSelection.files.length), 1)
                      ]),
                      _createElementVNode("li", _hoisted_22, [
                        _cache[45] || (_cache[45] = _createElementVNode("strong", null, "Projects Involved:", -1)),
                        _createTextVNode(" " + _toDisplayString(_unref(bsStore).bsSelection.projects.length), 1)
                      ]),
                      (_unref(bsStore).bsSelection.totalSize)
                        ? (_openBlock(), _createElementBlock("li", _hoisted_23, [
                            _cache[46] || (_cache[46] = _createElementVNode("strong", null, "Total size:", -1)),
                            _createTextVNode(" " + _toDisplayString(_unref(formatFileSize)(_unref(bsStore).bsSelection.totalSize)), 1)
                          ]))
                        : _createCommentVNode("", true)
                    ])
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", null, [
                _cache[48] || (_cache[48] = _createElementVNode("h5", { class: "mt-4" }, "Kit validation", -1)),
                _cache[49] || (_cache[49] = _createElementVNode("p", null, [
                  _createElementVNode("small", null, " Kits associate your samples with the correct barcodes and make sure the subsequent processing is accurate. Kits also contain storage credits that allow you to upload data to the Sampling Human analytics cloud. Do not exchange the correct kit or coupon codes if you run out of credits, contact support for assistance instead. This will ensure valid code association and prevent any disruptions in your workflow or data processing. ")
                ], -1)),
                _createVNode(CodeValidation, {
                  class: "mt-3",
                  ref_key: "codeValidation",
                  ref: codeValidation,
                  "label-text": "Kit\n          or coupon code",
                  "button-text": "Apply",
                  defaultCodes: [],
                  requireKit: false,
                  requireExplicitCounts: uploadFtp.value || uploadWebBcl.value || uploadWasabi.value,
                  requiredCouponTypeName: _unref(COUPON_TYPE_NAMES).STORAGE,
                  requriedCouponUnitsName: _unref(COUPON_UNITS).SAMPLES,
                  requiredCouponUnitsCount: minRequiredStorageCouponUnits.value,
                  requiredLicenseTypeName: null,
                  immediateValidation: true,
                  hideComponentMessage: true
                }, null, 8, ["requireExplicitCounts", "requiredCouponTypeName", "requriedCouponUnitsName", "requiredCouponUnitsCount"])
              ]),
              (codeValidation.value)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 5 }, [
                    (codeValidation.value.message)
                      ? (_openBlock(), _createBlock(_unref(BAlert), {
                          key: 0,
                          modelValue: codeValidation.value.failure,
                          "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((codeValidation.value.failure) = $event)),
                          variant: "danger",
                          class: "mt-3"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(codeValidation.value.message), 1)
                          ]),
                          _: 1
                        }, 8, ["modelValue"]))
                      : _createCommentVNode("", true),
                    _createVNode(_unref(BAlert), {
                      modelValue: codeValidation.value.success,
                      "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((codeValidation.value.success) = $event)),
                      variant: "success",
                      class: "mt-3"
                    }, {
                      default: _withCtx(() => [
                        _cache[50] || (_cache[50] = _createElementVNode("p", null, " Double-check the kit/coupon code before proceeding. Incorrect code association may lead to down-stream sample annotation and analysis errors. Correct kit selection provides details on barcode usage and other important information which is critical for achieving accurate results. ", -1)),
                        _cache[51] || (_cache[51] = _createElementVNode("p", null, " The upload action will consume storage credits associated with the kit/coupon code. This action is irreversible. ", -1)),
                        _createElementVNode("p", null, [
                          _createElementVNode("strong", null, "The following action will consume " + _toDisplayString(declaredStorageCouponUnits.value) + " storage credit(s). Beware that if more samples are uploaded, you may be charged for additional storage.", 1)
                        ])
                      ]),
                      _: 1
                    }, 8, ["modelValue"])
                  ], 64))
                : _createCommentVNode("", true),
              _createElementVNode("div", null, [
                _cache[62] || (_cache[62] = _createElementVNode("h5", { class: "mt-4" }, "Auto-Features", -1)),
                _createVNode(_unref(BAlert), {
                  modelValue: autoFeaturesDisabled.value,
                  "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((autoFeaturesDisabled).value = $event)),
                  variant: "secondary",
                  class: "mt-3",
                  show: ""
                }, {
                  default: _withCtx(() => [
                    (uploadWeb.value)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          _createTextVNode(" Auto-features are disabled for all web uploads. Please annotate your samples in the data manager when uploads are finished. ")
                        ], 64))
                      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                          _createTextVNode(" Auto-features are disabled when multipe kit/coupon codes are provided. Please annotate you samples manually later in the data manager. ")
                        ], 64))
                  ]),
                  _: 1
                }, 8, ["modelValue"]),
                (!uploadBasespace.value)
                  ? (_openBlock(), _createBlock(_unref(BFormCheckbox), {
                      key: 0,
                      id: "auto-sample-annotation",
                      modelValue: autoSampleAnnotation.value,
                      "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((autoSampleAnnotation).value = $event)),
                      disabled: autoFeaturesDisabled.value,
                      name: "auto-sample-annotation",
                      switch: "",
                      class: "mt-3"
                    }, {
                      default: _withCtx(() => [
                        _cache[52] || (_cache[52] = _createTextVNode(" Auto Sample Annotation ", -1)),
                        _createVNode(_component_font_awesome_icon, {
                          icon: ['fas', 'question-circle'],
                          class: "ms-2 text-secondary",
                          id: "icon-tooltip-auto-sample-annotation"
                        })
                      ]),
                      _: 1
                    }, 8, ["modelValue", "disabled"]))
                  : _createCommentVNode("", true),
                _createVNode(_unref(BFormCheckbox), {
                  id: "auto-project-definition",
                  modelValue: autoProjectDefinition.value,
                  "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((autoProjectDefinition).value = $event)),
                  disabled: autoFeaturesDisabled.value,
                  name: "auto-project-definition",
                  switch: "",
                  class: "mt-3"
                }, {
                  default: _withCtx(() => [
                    _cache[53] || (_cache[53] = _createTextVNode(" Auto Project Definition ", -1)),
                    _createVNode(_component_font_awesome_icon, {
                      icon: ['fas', 'question-circle'],
                      class: "ms-2 text-secondary",
                      id: "icon-tooltip-auto-project-definition"
                    })
                  ]),
                  _: 1
                }, 8, ["modelValue", "disabled"]),
                _createVNode(_unref(BFormCheckbox), {
                  id: "auto-sample-reconstruction",
                  modelValue: autoSampleReconstruction.value,
                  "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((autoSampleReconstruction).value = $event)),
                  disabled: autoFeaturesDisabled.value,
                  name: "auto-sample-reconstruction",
                  switch: "",
                  class: "mt-3"
                }, {
                  default: _withCtx(() => [
                    _cache[54] || (_cache[54] = _createTextVNode(" Auto Sample Reconstruction ", -1)),
                    _createVNode(_component_font_awesome_icon, {
                      icon: ['fas', 'question-circle'],
                      class: "ms-2 text-secondary",
                      id: "icon-tooltip-auto-sample-reconstruction"
                    })
                  ]),
                  _: 1
                }, 8, ["modelValue", "disabled"]),
                _createVNode(_unref(BTooltip), { target: "icon-tooltip-auto-sample-annotation" }, {
                  default: _withCtx(() => [...(_cache[55] || (_cache[55] = [
                    _createTextVNode(" Automatically annotate samples when upload is finished. ", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(_unref(BTooltip), { target: "icon-tooltip-auto-project-definition" }, {
                  default: _withCtx(() => [...(_cache[56] || (_cache[56] = [
                    _createTextVNode(" Automatically create project with annotated samples. ", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(_unref(BTooltip), { target: "icon-tooltip-auto-sample-reconstruction" }, {
                  default: _withCtx(() => [...(_cache[57] || (_cache[57] = [
                    _createTextVNode(" Automatically start the sample reconstruction process when project is defined. ", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(_unref(BCollapse), {
                  modelValue: autoSampleReconstruction.value,
                  "onUpdate:modelValue": _cache[21] || (_cache[21] = ($event: any) => ((autoSampleReconstruction).value = $event)),
                  class: "mt-3"
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("h6", _hoisted_24, [
                      _createVNode(_component_font_awesome_icon, {
                        icon: ['fas', 'server'],
                        class: "me-2"
                      }),
                      _cache[59] || (_cache[59] = _createTextVNode(" Pipeline selection ", -1)),
                      _createVNode(_component_font_awesome_icon, {
                        icon: ['fas', 'question-circle'],
                        class: "ms-2 text-secondary",
                        id: "pipeline-selection-help-icon"
                      }),
                      _createVNode(_unref(BTooltip), { target: "pipeline-selection-help-icon" }, {
                        default: _withCtx(() => [...(_cache[58] || (_cache[58] = [
                          _createElementVNode("div", { style: {"text-align":"justify"} }, [
                            _createElementVNode("p", null, " Automatic sample reconstruction will attempt to automatically reconstruct your samples based using the selected pipeline. ")
                          ], -1)
                        ]))]),
                        _: 1
                      })
                    ]),
                    _createVNode(PipelinePicker, {
                      class: "mt-3",
                      modelValue: selectedPipeline.value,
                      "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((selectedPipeline).value = $event)),
                      enabled: !nfCustomWorkflow.value,
                      label: null,
                      type: null,
                      premium: null,
                      activeOnly: true,
                      allowNullPipeline: false
                    }, null, 8, ["modelValue", "enabled"]),
                    (_unref(userStore).developer)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          _createElementVNode("label", _hoisted_25, [
                            _cache[61] || (_cache[61] = _createTextVNode(" Custom Workflow (developer option) ", -1)),
                            _createVNode(_component_font_awesome_icon, {
                              icon: ['fas', 'question-circle'],
                              id: "nf-workflow-location-help",
                              class: "ms-2 text-secondary"
                            }),
                            _createVNode(_unref(BTooltip), {
                              target: "nf-workflow-location-help",
                              class: "large-tooltip"
                            }, {
                              default: _withCtx(() => [...(_cache[60] || (_cache[60] = [
                                _createElementVNode("div", { style: {"text-align":"justify"} }, [
                                  _createElementVNode("p", null, " Specify the NextFlow workflow URL. Accepted options are: "),
                                  _createElementVNode("ul", null, [
                                    _createElementVNode("li", null, [
                                      _createElementVNode("code", null, " https://gitlab.samplinghuman.com/group/project/-/tree/ref/workflow-path ")
                                    ]),
                                    _createElementVNode("li", null, [
                                      _createElementVNode("code", null, " https://gitlab.samplinghuman.com/group/project/-/blob/ref/workflow-path/main.nf ")
                                    ]),
                                    _createElementVNode("li", null, [
                                      _createElementVNode("code", null, " gitlab://group/project/-/raw/ref/workflow-path/main.nf ")
                                    ]),
                                    _createElementVNode("li", null, [
                                      _createElementVNode("code", null, "gs://bucket/workflow-path/main.nf")
                                    ])
                                  ]),
                                  _createElementVNode("p", null, [
                                    _createTextVNode(" Best default for humans is to use the GitLab browser URL from the repository page. The backend canonicalizes that to the raw workflow source internally. Example browser URL: "),
                                    _createElementVNode("code", null, " https://gitlab.samplinghuman.com/samplinghuman/ax-stack/nextflow/pipelines/rnaseq-data-pipeline/-/tree/v0.1.0-dev "),
                                    _createTextVNode(" is automatically converted to canonical form: "),
                                    _createElementVNode("code", null, " gitlab://samplinghuman/ax-stack/nextflow/pipelines/rnaseq-data-pipeline/-/raw/v0.1.0-dev/main.nf "),
                                    _createTextVNode(" by the backend. ")
                                  ])
                                ], -1)
                              ]))]),
                              _: 1
                            })
                          ]),
                          _createVNode(_unref(BFormGroup), null, {
                            default: _withCtx(() => [
                              _createVNode(_unref(BInputGroup), null, {
                                prepend: _withCtx(() => [
                                  _createVNode(_unref(BButton), { variant: "outline-secondary" }, {
                                    default: _withCtx(() => [
                                      _createVNode(_unref(BFormCheckbox), {
                                        modelValue: nfCustomWorkflow.value,
                                        "onUpdate:modelValue": _cache[18] || (_cache[18] = ($event: any) => ((nfCustomWorkflow).value = $event)),
                                        name: "nf-custom-workflow"
                                      }, null, 8, ["modelValue"])
                                    ]),
                                    _: 1
                                  })
                                ]),
                                default: _withCtx(() => [
                                  _createVNode(_unref(BFormInput), {
                                    modelValue: nfWorkflowLocation.value,
                                    "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event: any) => ((nfWorkflowLocation).value = $event)),
                                    id: "nf-workflow-location",
                                    name: "nf-workflow-location",
                                    placeholder: "Enter workflow location",
                                    disabled: !nfCustomWorkflow.value,
                                    onChange: _cache[20] || (_cache[20] = ($event: any) => (nfCustomWorkflow.value = true))
                                  }, null, 8, ["modelValue", "disabled"])
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          })
                        ], 64))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]),
              (_unref(userStore).developer)
                ? (_openBlock(), _createBlock(DeveloperOptions, {
                    key: 6,
                    ref_key: "devOptions",
                    ref: devOptions,
                    size: "sm",
                    class: "mt-3"
                  }, null, 512))
                : _createCommentVNode("", true),
              _createVNode(_unref(BButton), {
                class: "mt-3",
                variant: "primary",
                type: "submit",
                disabled: 
              codeValidation.value?.failure ||
              (files.value.length === 0 && uploadWeb.value) ||
              uploadWebOddFastqFilesCount.value ||
              (linesFtp.value.length === 0 && uploadFtp.value) ||
              mutationInProgress.value
            
              }, {
                default: _withCtx(() => [
                  (uploadWeb.value)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        (mutationInProgress.value)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_26, [
                              _cache[63] || (_cache[63] = _createTextVNode(" Starting Upload ", -1)),
                              _createVNode(_unref(BSpinner), { small: "" })
                            ]))
                          : (mutationShowSuccess.value)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_27, [
                                _cache[64] || (_cache[64] = _createTextVNode(" Upload Started ", -1)),
                                _createVNode(_component_font_awesome_icon, {
                                  icon: ['fa', 'check-double'],
                                  class: "me-2"
                                })
                              ]))
                            : (_openBlock(), _createElementBlock("span", _hoisted_28, "Start Upload"))
                      ], 64))
                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        (mutationInProgress.value)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_29, [
                              _cache[65] || (_cache[65] = _createTextVNode(" Starting Transfer ", -1)),
                              _createVNode(_unref(BSpinner), { small: "" })
                            ]))
                          : (mutationShowSuccess.value)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_30, [
                                _cache[66] || (_cache[66] = _createTextVNode(" Transfer Started ", -1)),
                                _createVNode(_component_font_awesome_icon, {
                                  icon: ['fa', 'check-double'],
                                  class: "me-2"
                                })
                              ]))
                            : (_openBlock(), _createElementBlock("span", _hoisted_31, "Start Transfer"))
                      ], 64))
                ]),
                _: 1
              }, 8, ["disabled"]),
              _createVNode(_unref(BAlert), {
                modelValue: mutationError.value,
                "onUpdate:modelValue": _cache[22] || (_cache[22] = ($event: any) => ((mutationError).value = $event)),
                variant: "danger",
                class: "mt-3",
                dismissible: ""
              }, {
                default: _withCtx(() => [
                  _createElementVNode("strong", null, [
                    (uploadWeb.value)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_32, "Upload failed:"))
                      : (_openBlock(), _createElementBlock("span", _hoisted_33, "Transfer failed:"))
                  ]),
                  _createTextVNode(" " + _toDisplayString(mutationError.value), 1)
                ]),
                _: 1
              }, 8, ["modelValue"])
            ]),
            _: 1
          })
        ])
      ]),
      _: 1
    }, 8, ["title"])
  ]))
}
}

})